import React, { useState, useEffect, useCallback, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import API_BASE_URL from '../config';
import './ProductionPlanning.css';

const ProductionPlanning = () => {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedUserDefinedMethod, setSelectedUserDefinedMethod] = useState('Momentum Forecast'); // Default to Momentum Forecast
  const [availableUserDefinedMethods, setAvailableUserDefinedMethods] = useState([]);
  const [forecastData, setForecastData] = useState({
    actualInventory: [],
    salesForecast: [],
    productionPlan: [],
    arrivingInventory: [],
    predictedInventory: []
  });
  const [weeks, setWeeks] = useState([]);
  const [weekDates, setWeekDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tooltip, setTooltip] = useState({ show: false, text: '', x: 0, y: 0 });
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportData, setReportData] = useState({ weeks: [], products: [] });
  const [reportLoading, setReportLoading] = useState(false);
  const [updatingProductionPlan, setUpdatingProductionPlan] = useState(false);
  const [productionLeadTimeWeeks, setProductionLeadTimeWeeks] = useState(8); // Default fallback
  const [productionLeadTimeLoaded, setProductionLeadTimeLoaded] = useState(false);
  
  // Debug logging for production lead time
  console.log('Current productionLeadTimeWeeks state:', productionLeadTimeWeeks, 'Loaded:', productionLeadTimeLoaded);
  const tableContainerRef = useRef(null);

  // Calculate current week and target scroll position
  const getCurrentWeek = () => {
    const now = new Date();
    const year = now.getFullYear();
    const startOfYear = new Date(year, 0, 1);
    const days = Math.floor((now - startOfYear) / (24 * 60 * 60 * 1000));
    const week = Math.floor(days / 7) + 1;
    return `${year}-${week.toString().padStart(2, '0')}`;
  };

  const getTargetWeek = () => {
    const currentWeek = getCurrentWeek();
    const [year, week] = currentWeek.split('-');
    const targetWeekNum = Math.max(1, parseInt(week) - 6);
    return `${year}-${targetWeekNum.toString().padStart(2, '0')}`;
  };

  const isFutureWeek = (weekStr) => {
    const currentWeek = getCurrentWeek();
    return weekStr > currentWeek;
  };

  // Auto-scroll to target week
  const scrollToTargetWeek = useCallback((weekList) => {
    if (!tableContainerRef.current || weekList.length === 0) return;
    
    const targetWeek = getTargetWeek();
    const targetIndex = weekList.findIndex(week => week >= targetWeek);
    
    if (targetIndex !== -1) {
      const table = tableContainerRef.current.querySelector('table');
      if (table) {
        const headerCells = table.querySelectorAll('thead th');
        if (headerCells[targetIndex + 1]) { // +1 for the "Metric" column
          const cellWidth = headerCells[targetIndex + 1].offsetWidth;
          const scrollPosition = (targetIndex + 1) * cellWidth - 200; // 200px offset
          tableContainerRef.current.scrollLeft = Math.max(0, scrollPosition);
        }
      }
    }
  }, []);

  // Fetch production lead time from planning_config table
  useEffect(() => {
    const fetchProductionLeadTime = async () => {
      try {
        console.log('Fetching production lead time from:', `${API_BASE_URL}/api/planning-config`);
        const response = await axios.get(`${API_BASE_URL}/api/planning-config`);
        console.log('Planning config response:', response.data);
        
        if (response.data.success && response.data.configs) {
          const configs = response.data.configs;
          const leadTimeConfig = configs.find(config => config.config_name === 'production_lead_time_weeks');
          
          if (leadTimeConfig) {
            const leadTime = parseInt(leadTimeConfig.config_value) || 8;
            setProductionLeadTimeWeeks(leadTime);
            console.log('Production lead time loaded:', leadTime, 'weeks');
          } else {
            console.log('No production_lead_time_weeks found in config, using default: 8 weeks');
          }
        }
        
        // Mark as loaded regardless of whether we found a config or not
        setProductionLeadTimeLoaded(true);
        setError('');
      } catch (error) {
        console.error('Error fetching planning config:', error);
        console.log('Using default production lead time: 8 weeks');
        // Keep default value if config fetch fails
        setProductionLeadTimeLoaded(true);
      }
    };
    fetchProductionLeadTime();
  }, []);

  // Fetch products from sku_master table
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        console.log('Fetching products from:', `${API_BASE_URL}/api/products`);
        const response = await axios.get(`${API_BASE_URL}/api/products`);
        console.log('Products response:', response.data);
        setProducts(response.data);
        
        // Set default product to "Hair Care Bundle"
        const hairCareBundle = response.data.find(product => 
          product.IGD_Name && product.IGD_Name.toLowerCase().includes('hair care bundle')
        );
        if (hairCareBundle) {
          setSelectedProduct(hairCareBundle.IGD_Name);
        }
        
        setError('');
      } catch (error) {
        console.error('Error fetching products:', error);
        setError(`Failed to load products: ${error.message}`);
      }
    };
    fetchProducts();
  }, []);

  // Fetch available scenario forecast methods
  useEffect(() => {
    const fetchScenarioMethods = async () => {
      try {
        console.log('Fetching scenario forecast methods from:', `${API_BASE_URL}/api/forecast/methods`);
        const response = await axios.get(`${API_BASE_URL}/api/forecast/methods`);
        console.log('Scenario methods response:', response.data);
        setAvailableUserDefinedMethods(response.data.methods || []);
        setError('');
      } catch (error) {
        console.error('Error fetching scenario methods:', error);
        setError(`Failed to load scenario methods: ${error.message}`);
      }
    };
    fetchScenarioMethods();
  }, []);

  const fetchForecastData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      console.log('Fetching production planning data for:', selectedProduct, 'with forecast method:', selectedUserDefinedMethod);
      
      // Build query parameters
      const params = new URLSearchParams();
      if (selectedUserDefinedMethod && selectedUserDefinedMethod !== 'Momentum Forecast') {
        params.append('forecastMethod', selectedUserDefinedMethod);
      }
      
      const url = `${API_BASE_URL}/api/production-planning/${selectedProduct}?${params.toString()}`;
      console.log('Making request to:', url);
      
      const response = await axios.get(url);
      const data = response.data;
      console.log('Production planning data response:', data);
      
      setForecastData({
        actualInventory: data.actualInventory || [],
        salesForecast: data.salesForecast || [],
        productionPlan: data.productionPlan || [],
        arrivingInventory: data.arrivingInventory || [],
        predictedInventory: data.predictedInventory || []
      });
      
      // Extract unique weeks for table headers
      const allWeeks = new Set();
      [...data.actualInventory, ...data.salesForecast, ...data.productionPlan, ...data.arrivingInventory, ...data.predictedInventory]
        .forEach(item => allWeeks.add(item.week));
      const sortedWeeks = Array.from(allWeeks).sort();
      // Limit to 52 weeks to prevent overwhelming the display
      const limitedWeeks = sortedWeeks.slice(0, 52);
      setWeeks(limitedWeeks);
      
      // Set week dates if provided by API, otherwise calculate them
      if (data.weekDates) {
        setWeekDates(data.weekDates.slice(0, 52));
      } else {
        const calculatedDates = limitedWeeks.map(week => formatWeekDate(week));
        setWeekDates(calculatedDates);
      }
      
      // Auto-scroll to target week after data loads
      setTimeout(() => scrollToTargetWeek(limitedWeeks), 100);
      
    } catch (error) {
      console.error('Error fetching production planning data:', error);
      setError(`Failed to load production planning data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [selectedProduct, selectedUserDefinedMethod, scrollToTargetWeek, productionLeadTimeWeeks]);

  // Fetch forecast data when product or forecast method changes
  useEffect(() => {
    if (selectedProduct && productionLeadTimeLoaded) {
      console.log('Fetching forecast data - product selected and production lead time loaded');
      fetchForecastData();
    } else if (selectedProduct && !productionLeadTimeLoaded) {
      console.log('Product selected but production lead time not yet loaded, waiting...');
    }
  }, [selectedProduct, selectedUserDefinedMethod, fetchForecastData, productionLeadTimeLoaded]);

  const getValueForWeek = (dataArray, week, isSalesHistory = false) => {
    const item = dataArray.find(d => d.week === week);
    
    if (isSalesHistory && isFutureWeek(week)) {
      return null; // Return null for future sales history
    }
    
    if (item) {
      const value = Math.round(item.value);
      return value > 0 ? value : null; // Return null for 0 values to prevent graphing
    }
    
    return null;
  };

  const getDisplayValue = (value) => {
    return value !== null ? value : ''; // Show blank instead of "NULL" in table
  };

  const formatWeek = (weekStr) => {
    // Convert YYYY-WW format to more readable format
    const [year, week] = weekStr.split('-');
    return `W${week} ${year}`;
  };

  const formatWeekDate = (weekStr) => {
    // Convert YYYY-WW format to actual date (first day of the week)
    const [year, week] = weekStr.split('-');
    const yearNum = parseInt(year);
    const weekNum = parseInt(week);
    
    // Get January 1st of the year
    const jan1 = new Date(yearNum, 0, 1);
    
    // Find the first Monday of the year
    while (jan1.getDay() !== 1) { // 1 = Monday
      jan1.setDate(jan1.getDate() + 1);
    }
    
    // Add weeks to get to the target week
    const targetDate = new Date(jan1);
    targetDate.setDate(jan1.getDate() + (weekNum - 1) * 7);
    
    return targetDate.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getSelectedProductName = () => {
    const product = products.find(p => p.IGD_SKU_number === selectedProduct);
    return product ? product.IGD_Name : '';
  };

  // Color coding for production connections
  const getProductionColor = (colorKey) => {
    const colors = [
      '#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#feca57',
      '#ff9ff3', '#54a0ff', '#5f27cd', '#00d2d3', '#ff9f43',
      '#10ac84', '#ee5a24', '#2f3542', '#3742fa', '#ff6348'
    ];
    // Use the colorKey directly to generate consistent colors
    let hashValue = 0;
    for (let i = 0; i < colorKey.length; i++) {
      hashValue = ((hashValue << 5) - hashValue) + colorKey.charCodeAt(i);
      hashValue = hashValue & hashValue; // Convert to 32-bit integer
    }
    const colorIndex = Math.abs(hashValue) % colors.length;
    const color = colors[colorIndex];
    console.log(`ColorKey: ${colorKey}, Hash: ${hashValue}, Index: ${colorIndex}, Color: ${color}`);
    return color;
  };

  // Get consistent color key for production connections
  const getColorKey = (productionWeek, value) => {
    return `${productionWeek}-${value}`;
  };

  // Get the corresponding production week for an arrival week (configured weeks earlier)
  const getProductionWeekFromArrival = (arrivalWeek) => {
    console.log('getProductionWeekFromArrival called with arrivalWeek:', arrivalWeek, 'productionLeadTimeWeeks:', productionLeadTimeWeeks);
    const [year, week] = arrivalWeek.split('-');
    const yearNum = parseInt(year);
    const weekNum = parseInt(week);
    
    // Calculate the target week (configured weeks earlier)
    let targetWeekNum = weekNum - productionLeadTimeWeeks;
    let targetYear = yearNum;
    
    // Handle year boundary crossing
    if (targetWeekNum <= 0) {
      targetYear = yearNum - 1;
      // Calculate how many weeks into the previous year
      const weeksInPreviousYear = Math.abs(targetWeekNum);
      targetWeekNum = 52 - weeksInPreviousYear + 1;
    }
    
    const result = `${targetYear}-${targetWeekNum.toString().padStart(2, '0')}`;
    console.log('getProductionWeekFromArrival result:', result);
    return result;
  };

  // Get the corresponding arrival week for a production week (configured weeks later)
  const getArrivalWeekFromProduction = (productionWeek) => {
    console.log('getArrivalWeekFromProduction called with productionWeek:', productionWeek, 'productionLeadTimeWeeks:', productionLeadTimeWeeks);
    const [year, week] = productionWeek.split('-');
    const yearNum = parseInt(year);
    const weekNum = parseInt(week);
    
    // Calculate the target week (configured weeks later)
    let targetWeekNum = weekNum + productionLeadTimeWeeks;
    let targetYear = yearNum;
    
    // Handle year boundary crossing
    if (targetWeekNum > 52) {
      targetYear = yearNum + 1;
      targetWeekNum = targetWeekNum - 52;
    }
    
    const result = `${targetYear}-${targetWeekNum.toString().padStart(2, '0')}`;
    console.log('getArrivalWeekFromProduction result:', result);
    return result;
  };

  // Tooltip handlers
  const handleMouseEnter = (event, text) => {
    const rect = event.target.getBoundingClientRect();
    setTooltip({
      show: true,
      text,
      x: rect.left + rect.width / 2,
      y: rect.top - 10
    });
  };

  const handleMouseLeave = () => {
    setTooltip({ show: false, text: '', x: 0, y: 0 });
  };

  const fetchProductionReport = async () => {
    setReportLoading(true);
    try {
      // Build query parameters for forecast method
      const params = new URLSearchParams();
      if (selectedUserDefinedMethod && selectedUserDefinedMethod !== 'Momentum Forecast') {
        params.append('forecastMethod', selectedUserDefinedMethod);
      }
      
      const url = `${API_BASE_URL}/api/production-report?${params.toString()}`;
      console.log('Fetching production report with URL:', url);
      
      const response = await axios.get(url);
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching production report:', error);
      setError(`Failed to load production report: ${error.message}`);
    } finally {
      setReportLoading(false);
    }
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    // Add title
    doc.setFontSize(16);
    doc.text('Production Report', 14, 20);
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 30);
    
    // Prepare table data
    const tableData = [];
    const headers = ['Product Name', ...reportData.weeks.map((week, index) => {
      const weekNum = formatWeek(week);
      const weekDate = reportData.week_dates ? reportData.week_dates[index] : formatWeekDate(week);
      return `${weekNum}\n${weekDate}`;
    })];
    
    reportData.products.forEach(product => {
      const row = [product.IGD_Name];
      product.production_by_week.forEach(weekData => {
        row.push(weekData.quantity > 0 ? weekData.quantity.toString() : '');
      });
      tableData.push(row);
    });
    
    // Add table
    doc.autoTable({
      head: [headers],
      body: tableData,
      startY: 40,
      styles: {
        fontSize: 8,
        cellPadding: 2
      },
      headStyles: {
        fillColor: [66, 139, 202],
        textColor: 255
      },
      columnStyles: {
        0: { cellWidth: 60 } // Product name column
      }
    });
    
    // Save PDF
    doc.save('production-report.pdf');
  };

  const exportToCSV = () => {
    // Create CSV content with two header rows
    const weekHeaders = ['Product Name', ...reportData.weeks.map(week => formatWeek(week))];
    const dateHeaders = ['', ...reportData.weeks.map((week, index) => {
      return reportData.week_dates ? reportData.week_dates[index] : formatWeekDate(week);
    })];
    
    const csvContent = [weekHeaders.join(','), dateHeaders.join(',')];
    
    reportData.products.forEach(product => {
      const row = [product.IGD_Name];
      product.production_by_week.forEach(weekData => {
        row.push(weekData.quantity > 0 ? weekData.quantity.toString() : '');
      });
      csvContent.push(row.join(','));
    });
    
    // Create and download CSV file
    const blob = new Blob([csvContent.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'production-report.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openReportModal = () => {
    setShowReportModal(true);
    fetchProductionReport();
  };

  const handleUpdateProductionPlan = async () => {
    setUpdatingProductionPlan(true);
    setError('');
    try {
      console.log('Updating production plan with forecast method:', selectedUserDefinedMethod);
      
      // Build query parameters
      const params = new URLSearchParams();
      if (selectedUserDefinedMethod && selectedUserDefinedMethod !== 'Momentum Forecast') {
        params.append('forecastMethod', selectedUserDefinedMethod);
      }
      
      const response = await axios.post(`${API_BASE_URL}/api/production-planning/update?${params.toString()}`);
      console.log('Update response:', response.data);
      
      if (response.data.success) {
        // Refresh the production planning data after successful update
        await fetchForecastData();
        alert('Production Plan updated successfully!');
      } else {
        setError(`Failed to update production plan: ${response.data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating production plan:', error);
      setError(`Failed to update production plan: ${error.message}`);
    } finally {
      setUpdatingProductionPlan(false);
    }
  };

  return (
    <div className="production-planning-container">
      <div className="page-header">
        <div className="header-content">
          <div className="header-title">
            <h1>Production Planning Manager</h1>
          </div>
          <div className="header-tips">
            <ul>
              <li>Select product to view production planning data</li>
              <li>Compare different forecasting methods for production</li>
              <li>Update production plans and view inventory predictions</li>
            </ul>
          </div>
        </div>
        <div className="header-separator"></div>
      </div>

      <div className="page-content">
        {/* Custom Tooltip */}
        {tooltip.show && (
          <div 
            className="production-tooltip"
            style={{
              left: tooltip.x,
              top: tooltip.y,
              transform: 'translateX(-50%)'
            }}
          >
            {tooltip.text}
          </div>
        )}

        {/* Debug Display for Production Lead Time */}
        <div style={{ 
          background: '#f8f9fa', 
          padding: '10px', 
          margin: '10px 0', 
          borderRadius: '4px', 
          fontSize: '0.8rem',
          border: '1px solid #dee2e6'
        }}>
          <strong>Debug Info:</strong> Production Lead Time: {productionLeadTimeWeeks} weeks | 
          Loaded: {productionLeadTimeLoaded ? 'Yes' : 'No'} | 
          Product Selected: {selectedProduct ? 'Yes' : 'No'}
        </div>

        {/* Product Selector Pane - moved below header */}
        <div className="product-selector-pane">
          <div className="product-selector">
            <label htmlFor="product-select">Select Product:</label>
            <select
              id="product-select"
              value={selectedProduct}
              onChange={(e) => setSelectedProduct(e.target.value)}
              disabled={loading}
            >
              <option value="">Choose a product...</option>
              {products.map((product) => (
                <option key={product.IGD_SKU_number} value={product.IGD_SKU_number}>
                  {product.IGD_Name}
                </option>
              ))}
            </select>
          </div>
          
          {/* User-Defined Forecast Selector */}
          <div className="user-defined-forecast-selector">
            <label htmlFor="user-defined-forecast-select">User-Defined Forecast:</label>
            <select
              id="user-defined-forecast-select"
              value={selectedUserDefinedMethod}
              onChange={(e) => setSelectedUserDefinedMethod(e.target.value)}
              disabled={loading}
            >
              <option value="Momentum Forecast">Momentum Forecast</option>
              {availableUserDefinedMethods.map((method) => (
                <option key={method} value={method}>
                  {method}
                </option>
              ))}
            </select>
          </div>
          
          {/* Re-calculate Production Plan Button */}
          <div className="recalculate-production-plan-button">
            <button 
              onClick={handleUpdateProductionPlan}
              disabled={updatingProductionPlan || loading}
              className="update-production-plan-button"
            >
              {updatingProductionPlan ? 'Updating...' : 'Re-calculate Production Plan'}
            </button>
            <div className="production-plan-caution">
              <span style={{ color: 'red', fontSize: '0.8rem', fontWeight: 'bold' }}>
                ⚠️ Caution: this will overwrite all 'Planned' production amounts. If POs have been submitted for specific production, please mark them as scheduled on 'Production Schedule' tab first to ensure they are locked in.
              </span>
            </div>
          </div>
        </div>

        <div className="production-planning-content">
          <div className="production-planning-header">
            <h1>Production Planning</h1>
            <div className="report-icon-container">
              <button 
                className="report-icon-button"
                onClick={openReportModal}
                title="View Production Report"
              >
                See Production Report
              </button>
            </div>
          </div>

        {error && (
          <div className="error-message" style={{ color: 'red', padding: '10px', marginBottom: '20px' }}>
            <strong>Error:</strong> {error}
          </div>
        )}

        {loading && (
          <div className="loading">
            <p>Loading production planning data...</p>
          </div>
        )}

        {!loading && products.length === 0 && !error && (
          <div className="loading">
            <p>Loading products...</p>
          </div>
        )}

        {selectedProduct && !loading && (
          <>
            {/* Data Table */}
            <div className="forecast-table-container" ref={tableContainerRef}>
              <table className="forecast-table">
                <thead>
                  <tr>
                    <th className="sticky-header">Metric</th>
                    {weeks.map((week, index) => (
                      <th key={week}>
                        <div className="week-header">
                          <div className="week-number">{formatWeek(week)}</div>
                          <div className="week-date">{weekDates[index] || formatWeekDate(week)}</div>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="sticky-cell metric-label">Production Plan</td>
                    {weeks.map((week) => {
                      const value = getValueForWeek(forecastData.productionPlan, week);
                      const hasProduction = value !== null && value > 0;
                      const arrivalWeek = hasProduction ? getArrivalWeekFromProduction(week) : null;
                      const colorKey = hasProduction ? getColorKey(week, value) : null;
                      if (hasProduction) {
                        console.log(`Production Plan - Week: ${week}, Value: ${value}, ColorKey: ${colorKey}`);
                      }
                      return (
                        <td 
                          key={week} 
                          className={hasProduction ? 'production-connection' : ''}
                          style={{
                            backgroundColor: hasProduction ? getProductionColor(colorKey) + '20' : 'transparent',
                            borderLeft: hasProduction ? `3px solid ${getProductionColor(colorKey)}` : 'none'
                          }}
                          onMouseEnter={(event) => {
                            console.log('Production Plan tooltip - productionLeadTimeWeeks:', productionLeadTimeWeeks);
                            handleMouseEnter(event, `Production: ${value} units in week ${week}\nArrives: Week ${arrivalWeek} (${productionLeadTimeWeeks} weeks later)`);
                          }}
                          onMouseLeave={handleMouseLeave}
                        >
                          {getDisplayValue(value)}
                        </td>
                      );
                    })}
                  </tr>
                  <tr>
                    <td className="sticky-cell metric-label">Actual Inventory</td>
                    {weeks.map((week) => (
                      <td key={week}>{getDisplayValue(getValueForWeek(forecastData.actualInventory, week))}</td>
                    ))}
                  </tr>
                  <tr>
                    <td className="sticky-cell metric-label">(Less) Sales Forecast</td>
                    {weeks.map((week) => {
                      const value = getValueForWeek(forecastData.salesForecast, week);
                      return <td key={week}>{getDisplayValue(value !== null ? value * -1 : null)}</td>;
                    })}
                  </tr>
                  <tr>
                    <td className="sticky-cell metric-label">Arriving Inventory</td>
                    {weeks.map((week) => {
                      const value = getValueForWeek(forecastData.arrivingInventory, week);
                      const hasArrival = value !== null && value > 0;
                      
                      // Check if this arrival has a corresponding production plan (configured weeks earlier)
                      const productionWeek = hasArrival ? getProductionWeekFromArrival(week) : null;
                      const hasCorrespondingProduction = productionWeek && getValueForWeek(forecastData.productionPlan, productionWeek) > 0;
                      
                      // Only apply color coding if there's a corresponding production plan
                      const colorKey = hasArrival && hasCorrespondingProduction ? getColorKey(productionWeek, value) : null;
                      
                      // Determine tooltip text based on source
                      let tooltipText = '';
                      if (hasArrival) {
                        if (hasCorrespondingProduction) {
                          // From production
                          console.log('Arriving Inventory tooltip - productionLeadTimeWeeks:', productionLeadTimeWeeks);
                          tooltipText = `Arrives: ${value} units in week ${week}\nProduced: Week ${productionWeek} (${productionLeadTimeWeeks} weeks earlier)`;
                        } else {
                          // From other locations (Sundeep or Intransit to AWD)
                          tooltipText = `Arrives: ${value} units in week ${week}\nFrom: Inventory transfer (Sundeep/Intransit to AWD)`;
                        }
                      }
                      
                      if (hasArrival) {
                        console.log(`Arriving Inventory - Week: ${week}, Value: ${value}, ProductionWeek: ${productionWeek}, HasCorrespondingProduction: ${hasCorrespondingProduction}, ColorKey: ${colorKey}`);
                      }
                      
                      return (
                        <td 
                          key={week} 
                          className={hasArrival ? 'production-connection' : ''}
                          style={{
                            backgroundColor: hasArrival && hasCorrespondingProduction ? getProductionColor(colorKey) + '20' : 'transparent',
                            borderLeft: hasArrival && hasCorrespondingProduction ? `3px solid ${getProductionColor(colorKey)}` : 'none'
                          }}
                          onMouseEnter={hasArrival ? (event) => handleMouseEnter(event, tooltipText) : undefined}
                          onMouseLeave={hasArrival ? handleMouseLeave : undefined}
                        >
                          {getDisplayValue(value)}
                        </td>
                      );
                    })}
                  </tr>
                  <tr>
                    <td className="sticky-cell metric-label">Predicted Inventory</td>
                    {weeks.map((week) => (
                      <td key={week}>{getDisplayValue(getValueForWeek(forecastData.predictedInventory, week))}</td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Chart */}
            <div className="forecast-chart-container">
              <h2>Predicted Inventory Visualization</h2>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={weeks.map(week => ({
                  week: formatWeek(week),
                  'Predicted Inventory': getValueForWeek(forecastData.predictedInventory, week)
                }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="week" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="Predicted Inventory" 
                    stroke="#8884d8" 
                    strokeWidth={2}
                    dot={{ fill: '#8884d8' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </div>
      </div>

      {/* Production Report Modal */}
      {showReportModal && (
        <div className="modal-overlay" onClick={() => setShowReportModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Production Report</h2>
              <button 
                className="modal-close-button"
                onClick={() => setShowReportModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              {reportLoading ? (
                <div className="loading">Loading production report...</div>
              ) : (
                <>
                  <div className="report-controls">
                    <button className="export-button pdf" onClick={exportToPDF}>
                      📄 Export PDF
                    </button>
                    <button className="export-button csv" onClick={exportToCSV}>
                      📊 Export CSV
                    </button>
                  </div>
                  
                  <div className="report-table-container">
                    <table className="report-table">
                      <thead>
                        <tr>
                          <th>Product Name</th>
                          {reportData.weeks.map((week, index) => (
                            <th key={week}>
                              <div className="week-header">
                                <div className="week-number">{formatWeek(week)}</div>
                                <div className="week-date">{reportData.week_dates ? reportData.week_dates[index] : formatWeekDate(week)}</div>
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.products.map((product, index) => (
                          <tr key={index}>
                            <td className="product-name-cell">{product.IGD_Name}</td>
                            {product.production_by_week.map((weekData, weekIndex) => (
                              <td key={weekIndex} className={weekData.quantity > 0 ? 'has-production' : ''}>
                                {weekData.quantity > 0 ? weekData.quantity : ''}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductionPlanning; 